/**
 * <{brand}-search> — standalone AI search box custom element. Lives anywhere on
 * the host page (e.g. a header search bar), independent of the chat widget: the
 * only link between the two is search-share.ts (theme accent + default strings),
 * never a direct import of chat-widget.ts (would drag the whole chat bundle in
 * and risk an import cycle).
 *
 * Submitting a query opens the chat widget with that query auto-sent
 * (`source: 'search'` so the started conversation records where it came from,
 * see public-api.ts KobotOpenOptions + chat-widget.ts ensureConversation).
 *
 * Attributes `placeholder` / `button-label` (explicit host intent) always win
 * over the tenant defaults published via search-share; with neither, the
 * element falls back to its own neutral built-in PL strings (DEFAULT_PLACEHOLDER
 * / DEFAULT_BUTTON_LABEL) — a pre-bootstrap-only fallback, since it must render
 * something sensible even before the chat widget has bootstrapped (or if it
 * isn't on the page at all). Once the chat widget bootstraps, its server i18n
 * (searchPlaceholder/searchButton, tenant-locale-aware) overrides these via
 * search-share — the built-ins are never a substitute for real localization.
 */
import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SEARCH_TAG } from '../brand.js';
import { open } from '../public-api.js';
import { subscribeSearchContext, type SearchContext } from '../search-share.js';

const DEFAULT_PLACEHOLDER = 'Zapytaj asystenta AI…';
const DEFAULT_BUTTON_LABEL = 'Szukaj';

@customElement(SEARCH_TAG)
export class SearchBox extends LitElement {
  static styles = css`
    :host {
      display: block;
      max-width: 100%;
    }
    form {
      display: flex;
      gap: 8px;
      box-sizing: border-box;
    }
    input {
      flex: 1;
      min-width: 0;
      padding: 10px 14px;
      border: 1px solid #ccc;
      border-radius: 8px;
      font-size: 15px;
      font-family: inherit;
      box-sizing: border-box;
      /* type="search" carries its own OS chrome in Safari (rounded pill, no
         border) unless reset — keep it visually identical to a plain text input. */
      -webkit-appearance: none;
      appearance: none;
    }
    input:focus-visible {
      outline: 2px solid var(--search-accent, #333);
      outline-offset: 1px;
    }
    button {
      flex-shrink: 0;
      padding: 10px 18px;
      border: none;
      border-radius: 8px;
      background: var(--search-accent, #333);
      color: var(--search-accent-ink, #fff);
      font-size: 15px;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      white-space: nowrap;
      transition: opacity 0.15s;
    }
    button:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    button:focus-visible {
      outline: 2px solid var(--search-accent, #333);
      outline-offset: 2px;
    }
  `;

  /** Explicit host override — wins over the tenant default from search-share. */
  @property({ attribute: 'placeholder' }) placeholder = '';

  /** Explicit host override — wins over the tenant default from search-share. */
  @property({ attribute: 'button-label' }) buttonLabel = '';

  @state() private query = '';

  /** Tenant accent/strings pushed by ChatWidget post-bootstrap; null = not published (yet). */
  @state() private ctx: SearchContext | null = null;

  private unsubscribe: (() => void) | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this.unsubscribe = subscribeSearchContext((ctx) => {
      this.ctx = ctx;
      // '' = tenant hasn't set/shipped this yet — keep the built-in CSS fallback
      // (var(--search-accent, #333) / var(--search-accent-ink, #fff)) rather
      // than clobbering it with an empty value.
      if (ctx.accent) this.style.setProperty('--search-accent', ctx.accent);
      if (ctx.accentInk) this.style.setProperty('--search-accent-ink', ctx.accentInk);
    });
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private get effectivePlaceholder(): string {
    return this.placeholder || this.ctx?.placeholder || DEFAULT_PLACEHOLDER;
  }

  private get effectiveButtonLabel(): string {
    return this.buttonLabel || this.ctx?.buttonLabel || DEFAULT_BUTTON_LABEL;
  }

  private handleInput(e: Event): void {
    this.query = (e.target as HTMLInputElement).value;
  }

  /** Enter in the input: handle directly instead of letting it fall through to
   *  the form's native submit — a single path (vs. Enter AND @submit both
   *  firing in real browsers) keeps this predictable across environments.
   *  isComposing guards IME (e.g. typing Japanese/Chinese): the Enter that
   *  confirms a candidate must not also submit the form. */
  private handleKeydown(e: KeyboardEvent): void {
    if (e.key !== 'Enter' || e.isComposing) return;
    e.preventDefault();
    this.submitQuery();
  }

  private handleSubmit(e: SubmitEvent): void {
    e.preventDefault();
    this.submitQuery();
  }

  private submitQuery(): void {
    const q = this.query.trim();
    if (!q) return;
    open({ message: q, source: 'search' });
    this.query = '';
  }

  render() {
    const label = this.effectiveButtonLabel;
    return html`
      <form role="search" @submit=${this.handleSubmit}>
        <input
          type="search"
          enterkeyhint="search"
          .value=${this.query}
          @input=${this.handleInput}
          @keydown=${this.handleKeydown}
          placeholder=${this.effectivePlaceholder}
          aria-label=${this.effectivePlaceholder}
        />
        <button type="submit" ?disabled=${this.query.trim() === ''} aria-label=${label}>${label}</button>
      </form>
    `;
  }
}

// Custom element tag is brand-derived at build time (SEARCH_TAG), so it can't be
// a literal key in HTMLElementTagNameMap — the augmentation is intentionally omitted
// (mirrors chat-widget.ts / WIDGET_TAG).
